/**
 * useMessages Hook
 *
 * Fetches and manages chat messages for a conversation in inbox-v3.
 * Uses the /api/v1/conversations/:id/messages endpoint with long polling support.
 */

import {useState, useEffect, useCallback, useRef} from 'react'
import {
  useLazyGetConversationMessagesQuery,
  useGetConversationMessagesQuery,
} from '../../../../../services/cellcast'
import {mapApiMessages, mapApiPagination} from '../services/inboxMapper'
import {Message, MessagePagination} from '../types/inbox-v3.types'

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if two messages are likely the same message based on content and timing.
 * Used for deduplication when _id comparison is not reliable (e.g., V2 vs V3 API messages).
 */
const isSameMessage = (msg1: Message, msg2: Message): boolean => {
  // If both have _id and they match, they're the same
  if (msg1._id && msg2._id && msg1._id === msg2._id) {
    return true
  }

  // Compare by content: same message text, sender, receiver, and within 60 seconds
  if (msg1.message !== msg2.message) {
    return false
  }
  if (msg1.sender !== msg2.sender) {
    return false
  }
  if (msg1.receiver !== msg2.receiver) {
    return false
  }

  // Check if timestamps are within 60 seconds of each other
  const time1 = new Date(msg1.createdAt).getTime()
  const time2 = new Date(msg2.createdAt).getTime()
  const timeDiff = Math.abs(time1 - time2)

  return timeDiff < 60000 // 60 seconds tolerance
}

/**
 * Check if a message already exists in the messages array
 */
const messageExists = (messages: Message[], newMessage: Message): boolean => {
  return messages.some((existing) => isSameMessage(existing, newMessage))
}

// ============================================================================
// Types
// ============================================================================

export interface UseMessagesOptions {
  /** The conversation ID to fetch messages for */
  conversationId?: string | null
  /** Whether to automatically fetch messages when conversationId changes (default: true) */
  autoFetch?: boolean
  /** Number of messages to fetch per page (default: 20) */
  limit?: number
  /** Polling interval in ms (0 = disabled, default: 30000) */
  pollingInterval?: number
}

export interface UseMessagesReturn {
  /** Array of messages for the conversation */
  messages: Message[]
  /** Whether initial load is in progress */
  isLoading: boolean
  /** Whether a fetch is currently in progress */
  isFetching: boolean
  /** Whether there are more messages to load */
  hasMore: boolean
  /** Pagination information */
  pagination: MessagePagination
  /** Error message if fetch failed */
  error: string | null
  /** Fetch initial messages */
  fetchMessages: () => Promise<void>
  /** Fetch more messages (infinite scroll) */
  fetchMore: () => Promise<void>
  /** Refresh messages from the beginning */
  refresh: () => Promise<void>
  /** Add a message to the end of the list (for optimistic updates) */
  addMessage: (message: Message) => void
  /** Prepend a message to the beginning of the list */
  prependMessage: (message: Message) => void
  /** Fetch and prepend a message by ID */
  fetchAndPrependMessage: (messageId: string) => Promise<void>
  /** Update a message in the list (for status updates) */
  updateMessage: (messageId: string, updates: Partial<Message>) => void
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useMessages(options: UseMessagesOptions): UseMessagesReturn {
  const {conversationId, autoFetch = true, limit = 20, pollingInterval = 30000} = options

  // State
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<MessagePagination>({
    current: 1,
    total: 0,
    totalPages: 1,
    limit,
    hasMore: true,
  })

  // Track current page for pagination
  const pageRef = useRef(1)

  // Track if we've done the initial fetch
  const hasInitialFetchRef = useRef(false)

  // ============================================================================
  // RTK Query - Long Polling for New Messages
  // ============================================================================

  // Use the query hook with pollingInterval for automatic long polling
  // This will refetch the first page of messages at the specified interval
  const {
    data: pollingData,
    isFetching: isPollingFetching,
    refetch: refetchPolling,
  } = useGetConversationMessagesQuery(
    {
      conversationId: conversationId || '',
      page: 1,
      limit,
    },
    {
      // Only enable polling if we have a valid conversationId and polling is enabled
      skip: !conversationId || !autoFetch,
      pollingInterval: pollingInterval > 0 ? pollingInterval : 0,
      // Refetch when conversationId changes
      refetchOnMountOrArgChange: true,
    }
  )

  // Lazy query for manual fetches (initial load, pagination, etc.)
  const [fetchConversationMessages, {isFetching: isManualFetching}] =
    useLazyGetConversationMessagesQuery()

  // Combined fetching state
  const isFetching = isPollingFetching || isManualFetching

  // ============================================================================
  // Handle Polling Data Updates
  // ============================================================================

  // Update messages when polling returns new data
  useEffect(() => {
    if (!pollingData?.data?.messages || !hasInitialFetchRef.current) {
      return
    }

    const polledMessages = pollingData.data.messages
    const mappedPolledMessages = mapApiMessages(polledMessages)

    setMessages((prevMessages) => {
      // Find new messages that don't exist in current list (using content-based deduplication)
      const newMessages: Message[] = []
      const updatedExisting: Map<string, Message> = new Map()

      for (const polledMsg of mappedPolledMessages) {
        // Check if this message already exists using content-based comparison
        const existingMsgIndex = prevMessages.findIndex((m) => isSameMessage(m, polledMsg))

        if (existingMsgIndex === -1) {
          // New message - add to newMessages array
          newMessages.push(polledMsg)
        } else {
          // Existing message - check for status update
          const existingMsg = prevMessages[existingMsgIndex]
          if (existingMsg.status !== polledMsg.status) {
            // Track for update using the existing message's _id
            updatedExisting.set(existingMsg._id, polledMsg)
          }
        }
      }

      // If no changes, return previous messages (no re-render)
      if (newMessages.length === 0 && updatedExisting.size === 0) {
        return prevMessages
      }

      // Build updated messages list
      let updatedMessages = prevMessages.map((msg) => {
        const updated = updatedExisting.get(msg._id)
        return updated || msg
      })

      // Prepend new messages at the beginning (newest first)
      if (newMessages.length > 0) {
        // Sort new messages by date descending and prepend
        newMessages.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
        updatedMessages = [...newMessages, ...updatedMessages]
      }

      return updatedMessages
    })
  }, [pollingData])

  // ============================================================================
  // Fetch Functions
  // ============================================================================

  /**
   * Fetch initial messages for the conversation
   */
  const fetchMessages = useCallback(async () => {
    if (!conversationId) {
      setMessages([])
      return
    }

    setIsLoading(true)
    setError(null)
    pageRef.current = 1
    hasInitialFetchRef.current = false

    try {
      const response = await fetchConversationMessages({
        conversationId,
        page: 1,
        limit,
      }).unwrap()

      const items = response?.data?.messages || []
      const mappedMessages = mapApiMessages(items)
      const paginationInfo = response?.data?.pagination
        ? mapApiPagination(response.data.pagination)
        : {current: 1, total: items.length, totalPages: 1, limit, hasMore: false}

      setMessages(mappedMessages)
      setPagination(paginationInfo)
      setHasMore(paginationInfo.hasMore)
      hasInitialFetchRef.current = true
    } catch (err: any) {
      console.error('Error fetching messages:', err)
      setError(err?.message || 'Failed to fetch messages')
      setMessages([])
    } finally {
      setIsLoading(false)
    }
  }, [conversationId, limit, fetchConversationMessages])

  /**
   * Fetch more messages for infinite scroll
   */
  const fetchMore = useCallback(async () => {
    if (!conversationId || !hasMore || isFetching) {
      return
    }

    const nextPage = pageRef.current + 1

    try {
      const response = await fetchConversationMessages({
        conversationId,
        page: nextPage,
        limit,
      }).unwrap()

      const items = response?.data?.messages || []

      if (items.length === 0) {
        setHasMore(false)
        return
      }

      const mappedMessages = mapApiMessages(items)
      const paginationInfo = response?.data?.pagination
        ? mapApiPagination(response.data.pagination)
        : {current: nextPage, total: 0, totalPages: 1, limit, hasMore: false}

      setMessages((prev) => [...prev, ...mappedMessages])
      setPagination(paginationInfo)
      setHasMore(paginationInfo.hasMore)
      pageRef.current = nextPage
    } catch (err: any) {
      console.error('Error fetching more messages:', err)
      setError(err?.message || 'Failed to fetch more messages')
    }
  }, [conversationId, limit, hasMore, isFetching, fetchConversationMessages])

  /**
   * Refresh messages from the beginning
   */
  const refresh = useCallback(async () => {
    await fetchMessages()
    // Also trigger the polling query to refetch
    refetchPolling()
  }, [fetchMessages, refetchPolling])

  /**
   * Add a message to the end of the list (for optimistic updates)
   */
  const addMessage = useCallback((message: Message) => {
    setMessages((prev) => [...prev, message])
  }, [])

  /**
   * Prepend a message to the beginning of the list
   */
  const prependMessage = useCallback((message: Message) => {
    setMessages((prev) => {
      // Check if message already exists using content-based deduplication
      if (messageExists(prev, message)) {
        return prev
      }
      return [message, ...prev]
    })
  }, [])

  /**
   * Update a message in the list (for status updates)
   */
  const updateMessage = useCallback((messageId: string, updates: Partial<Message>) => {
    setMessages((prev) => prev.map((msg) => (msg._id === messageId ? {...msg, ...updates} : msg)))
  }, [])

  /**
   * Fetch and prepend a message by ID
   * Used after sending a message to get the full message details
   * Note: This fetches all messages and finds the new one to prepend
   */
  const fetchAndPrependMessage = useCallback(
    async (messageId: string) => {
      if (!conversationId) return

      try {
        // Fetch the first page to get the latest messages including the new one
        const response = await fetchConversationMessages({
          conversationId,
          page: 1,
          limit: 10,
        }).unwrap()

        const items = response?.data?.messages || []
        const newMessage = items.find((msg: any) => msg._id === messageId)

        if (newMessage) {
          const mappedMessages = mapApiMessages([newMessage])
          if (mappedMessages.length > 0) {
            prependMessage(mappedMessages[0])
          }
        }
      } catch (err: any) {
        console.error('Error fetching message details:', err)
      }
    },
    [conversationId, fetchConversationMessages, prependMessage]
  )

  // ============================================================================
  // Effects
  // ============================================================================

  // Fetch messages when conversationId changes
  useEffect(() => {
    if (autoFetch && conversationId) {
      fetchMessages()
    } else if (!conversationId) {
      setMessages([])
      setHasMore(true)
      pageRef.current = 1
      hasInitialFetchRef.current = false
    }
  }, [conversationId, autoFetch, fetchMessages])

  // ============================================================================
  // Return
  // ============================================================================

  return {
    messages,
    isLoading,
    isFetching,
    hasMore,
    pagination,
    error,
    fetchMessages,
    fetchMore,
    refresh,
    addMessage,
    prependMessage,
    fetchAndPrependMessage,
    updateMessage,
  }
}